import {getUtmParameters, mParticleTrackSessionAttributes} from '@treatwell/ui';
import React from 'react';
import {selectChannelData} from '../store/slices/channel/slice';
import {selectVenueId} from '../store/slices/venue/slice';
import {
  selectAvailableDaysFirstWeek,
  selectSelectedEmployee,
  selectServices,
  selectSkusDuration,
  selectTimeslotsInitialDate,
} from '../store/slices/selectors';
import {ANY_EMPLOYEE} from '../store/slices/constants';
import {useAppSelector} from './store';
import {countDiscountedTimeslots} from '../components/TimeSlotList/countDiscountedTimeslots';
import {useGenerateDayWithAvailabilityFromInitialDate} from './generateDayWithAvailabilityFromInitialDate';
import {SDKEventAttrs} from '@mparticle/web-sdk';
import {trackPageViewEvent} from '../utils/mParticleTracking';

export function useTrackPageAndSessionView(
  pageType: 'availability' | 'reschedule',
) {
  const channel = useAppSelector(selectChannelData);
  const countryCode = channel.country.countryCode;
  const venueId = useAppSelector(selectVenueId);
  const services = useAppSelector(selectServices);
  const selectedEmployee = useAppSelector(selectSelectedEmployee);
  const isDefaultEmployee = selectedEmployee === ANY_EMPLOYEE;
  const totalDuration = useAppSelector(selectSkusDuration);
  const initialDateTimeslots = useAppSelector(selectTimeslotsInitialDate);
  const discountedInitialDateTimeslotsCount =
    countDiscountedTimeslots(initialDateTimeslots);
  const daysFromInitialDate = useGenerateDayWithAvailabilityFromInitialDate();
  const availableDaysFirstWeek = useAppSelector(selectAvailableDaysFirstWeek);
  const isPageViewEventSent = React.useRef(false);

  React.useEffect(() => {
    if (isPageViewEventSent.current) {
      return;
    }

    const utmParameters = getUtmParameters(window.location.search);

    const attributes: SDKEventAttrs = {
      country: countryCode,
      referrer: document.referrer,
      url: window.location.href,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      url_path: window.location.pathname,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      query_parameters: window.location.search,
      platform: 'web',
      section: 'booking_flow',
      venue_id: venueId,
      ...utmParameters,
    };

    if (pageType === 'availability') {
      attributes.item_count = services.length;
      attributes.selected_employee_count = isDefaultEmployee ? 0 : 1;
      attributes.is_default_employee = isDefaultEmployee;
      attributes.total_duration = totalDuration;
      attributes.available_day_count = availableDaysFirstWeek;
      attributes.time_slot_count = initialDateTimeslots.length;
      attributes.no_availability = initialDateTimeslots.length === 0;
      attributes.days_till_next_available_date = daysFromInitialDate;

      if (initialDateTimeslots.length !== 0) {
        attributes.discount_time_slot_count =
          discountedInitialDateTimeslotsCount;
      }
    }

    trackPageViewEvent({eventName: pageType, eventAttributes: attributes});
    mParticleTrackSessionAttributes(countryCode);
    isPageViewEventSent.current = true;
  }, [
    countryCode,
    venueId,
    pageType,
    services.length,
    isDefaultEmployee,
    totalDuration,
    initialDateTimeslots.length,
    discountedInitialDateTimeslotsCount,
    daysFromInitialDate,
    availableDaysFirstWeek,
  ]);
}
